// Componentes de UI reutilizáveis

export class UIComponents {
  private static toastContainer: HTMLElement | null = null;

  static init() {
    // Criar container de toasts se não existir
    if (!this.toastContainer) {
      this.toastContainer = document.createElement('div');
      this.toastContainer.id = 'toast-container';
      this.toastContainer.className = 'toast-container';
      document.body.appendChild(this.toastContainer);
    }

    // indicador offline removido a pedido do usuário
  }

  // ========== LOADING ==========
  static showLoading(message = 'Carregando...'): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = 'loading-overlay';
    overlay.innerHTML = `
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>${message}</p>
      </div>
    `;
    document.body.appendChild(overlay);
    return overlay;
  }

  static hideLoading(overlay: HTMLElement) {
    overlay.remove();
  }

  // ========== TOAST NOTIFICATIONS ==========
  static toast(
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info',
    duration = 4000
  ) {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;

    const icons = {
      success: '✓',
      error: '✕',
      info: 'ℹ',
      warning: '⚠'
    };

    toast.innerHTML = `
      <span class="toast-icon">${icons[type]}</span>
      <span class="toast-message">${message}</span>
    `;

    this.toastContainer?.appendChild(toast);

    // Animar entrada
    setTimeout(() => toast.classList.add('show'), 10);

    // Remover após duração
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  static success(message: string) {
    this.toast(message, 'success');
  }

  static error(message: string) {
    this.toast(message, 'error', 6000);
  }

  static warning(message: string) {
    this.toast(message, 'warning', 5000);
  }

  static info(message: string) {
    this.toast(message, 'info');
  }

  // ========== CONFIRMAÇÃO ==========
  static async confirm(
    message: string,
    title = 'Confirmar'
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const dialog = document.createElement('div');
      dialog.className = 'confirm-dialog-overlay';
      dialog.innerHTML = `
        <div class="confirm-dialog">
          <h3>${title}</h3>
          <p>${message}</p>
          <div class="confirm-actions">
            <button class="btn ghost" data-action="cancel">Cancelar</button>
            <button class="btn primary" data-action="confirm">Confirmar</button>
          </div>
        </div>
      `;

      dialog.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const action = target.dataset.action;

        if (action === 'confirm') {
          resolve(true);
          dialog.remove();
        } else if (action === 'cancel' || target === dialog) {
          resolve(false);
          dialog.remove();
        }
      });

      document.body.appendChild(dialog);
    });
  }

  static atualizarContadorPendentes(count: number) {
    const badge = document.getElementById('pending-count');
    const indicator = document.getElementById('offline-indicator');

    if (badge) {
      badge.textContent = count.toString();
      badge.style.display = count > 0 ? 'inline-block' : 'none';
    }

    // Só mostrar indicador se realmente offline OU se tiver itens pendentes
    if (indicator) {
      if (!navigator.onLine || count > 0) {
        indicator.classList.add('show');
      } else {
        indicator.classList.remove('show');
      }
    }
  }

  // ========== SINCRONIZAÇÃO ==========
  static mostrarSincronizando() {
    const indicator = document.getElementById('offline-indicator');
    if (indicator) {
      indicator.classList.add('syncing');
      const icon = indicator.querySelector('.material-icons-round');
      if (icon) {
        icon.textContent = 'sync';
      }
    }
  }

  static ocultarSincronizando() {
    const indicator = document.getElementById('offline-indicator');
    if (indicator) {
      indicator.classList.remove('syncing');
      const icon = indicator.querySelector('.material-icons-round');
      if (icon && !navigator.onLine) {
        icon.textContent = 'cloud_off';
      }
    }
  }

  // ========== RESULTADOS VAZIOS ==========
  static emptyState(message: string, icon = 'inbox'): string {
    return `
      <div class="empty-state">
        <span class="material-icons-round">${icon}</span>
        <p>${message}</p>
      </div>
    `;
  }

  // ========== ERRO ==========
  static errorState(message: string, onRetry?: () => void): HTMLElement {
    const container = document.createElement('div');
    container.className = 'error-state';
    container.innerHTML = `
      <span class="material-icons-round">error_outline</span>
      <p>${message}</p>
      ${onRetry ? '<button class="btn primary" id="retry-btn">Tentar novamente</button>' : ''}
    `;

    if (onRetry) {
      const retryBtn = container.querySelector('#retry-btn');
      retryBtn?.addEventListener('click', onRetry);
    }

    return container;
  }

  // ========== SKELETON LOADING ==========
  static skeleton(type: 'card' | 'list' | 'text' = 'card'): string {
    if (type === 'card') {
      return `
        <div class="skeleton skeleton-card">
          <div class="skeleton-line" style="width: 60%;"></div>
          <div class="skeleton-line" style="width: 80%;"></div>
        </div>
      `;
    }

    if (type === 'list') {
      return Array(5).fill(`
        <div class="skeleton skeleton-list-item">
          <div class="skeleton-line" style="width: 40%;"></div>
          <div class="skeleton-line" style="width: 90%;"></div>
        </div>
      `).join('');
    }

    return '<div class="skeleton skeleton-line"></div>';
  }
}

// Inicializar automaticamente quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => UIComponents.init());
} else {
  UIComponents.init();
}

export default UIComponents;
